import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel"; // 조회조건 Panel
import { fnGetDataSet, fnSave } from "@shared/api/restApi"; // BackEnd API
import {
  getInitFormData,
  applyAutoSlip,
  fnGetIotype,
} from "@shared/utils/CommonUtils"; // 공통 유틸 메뉴 초기화(조회조건, 그리드, 프리폼) 설정 load 관련 내용
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import { showDialog } from "@widgets/CommonDialog"; // Message Box
import { global } from "@shared/libs/Global"; // Login User관련 전역 변수( 로그인 정보)
import SnGrid from "@shared/components/GridV2/SnGrid"; // 공통 그리드
import { menuInfo } from "@shared/type/CommonType"; // 공통 Interface
import { showResizablePopupModal } from "@widgets/CommonModal";
import SN_MTL_SCT_REG_001_POP_001 from "@menu/MTL/SN_MTL_SCT_REG_001/SN_MTL_SCT_REG_001_POP_001";
import SnButton from "@shared/components/SnButton";
import SnResizeBar from "@shared/components/SnResizeBar";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import { dateToString } from "@shared/utils/DateUtils";
import { RowNode } from "ag-grid-community";

interface Props {
  Menuprops: menuInfo;
}

const SN_MTL_SCT_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
    grid2: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const searchRef = useRef<any>(null);
  const { userInfo } = global(); // Login user 정보
  const iotypeRef = useRef<any[]>([]);
  //#endregion

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [selectedDocu, setSelectedDocu] = useState<string>("");
  //#endregion

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useEffect(() => {
    if (
      Menuprops.figure &&
      columns.grid1.length > 0 &&
      columns.grid2.length > 0
    ) {
      onActivate(Menuprops.nodeData);
    }
  }, [columns, Menuprops]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await fnGetIotype("MTL", "", "5", "");
      iotypeRef.current = data;
    };
    fetchData();
  }, []);

  //#region  Button Event
  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  //#region onSearch
  const onSearch = async (searchData: any) => {
    const modifiedRowsH = getGridCurrent("grid1").getChange();
    const modifiedRowsL = getGridCurrent("grid2").getChange();
    if (
      modifiedRowsH.modifiedRowcount > 0 ||
      modifiedRowsL.modifiedRowcount > 0
    ) {
      const confirm = await showDialog(
        "confirmCancel",
        "수정된 내용이 있습니다. 저장하시겠습니까?"
      );
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) {
          return;
        }
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_mtl_sct_reg_001_01",
        params: searchDataToUse,
      };

      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1] || [],
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region onActivate
  const onActivate = async (nodeData: any) => {
    if (!nodeData) return;
    if ((Menuprops.figure as string) === "fg_drilldown.Search") {
      const selectedData = {
        cd_firm: nodeData.cd_firm,
        cd_biz: nodeData.cd_biz,
        no_io: nodeData.no_io,
        dt_from: nodeData.dt_io,
        dt_to: nodeData.dt_io,
      };
      const searchData = {
        ...formData,
        ...selectedData,
      };
      setFormData(searchData);
      onSearch(searchData);
    }
  };
  //#endregion

  //#region onInsert
  const onInsert = () => {
    try {
      getGridCurrent("grid1").addRow();
    } catch (error: any) {}
  };
  //#endregion

  //#region onAddRow
  const onAddRow = () => {
    try {
      return getGridCurrent("grid2").addRow();
    } catch (error: any) {}
  };
  //#endregion

  //#region onDelete
  const onDelete = async () => {
    if (getGridCurrent("grid1").getDisplayedRowCount() < 1) {
      return;
    }

    const confirm = await showDialog("confirm", "삭제하시겠습니까?");

    if (!confirm) {
      return; // 사용자가 아니오를 선택하면 함수 종료
    }

    getGridCurrent("grid2").deleteAllRows();
    getGridCurrent("grid1").deleteSelectedRows();

    const realDeletedRows = getGridCurrent("grid1").getChange().delete ?? [];
    if (Array.isArray(realDeletedRows) && realDeletedRows.length < 1) {
      // 실제로 삭제된 행이 없으면 변경 사항을 적용하고 반환합니다.
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      return;
    }

    return await onSave();
  };
  //endregion

  //#region OnDeleteRow
  const onDeleteRow = () => {
    if (getGridCurrent("grid2").getDisplayedRowCount() < 1) {
      return;
    }

    getGridCurrent("grid2").deleteSelectedRows();
  };
  //#endregion

  //#region onSave
  const onSave = async () => {
    try {
      getGridCurrent("grid1").stopEditing();
      getGridCurrent("grid2").stopEditing();
      // H 필수값 체크
      const requiredValuesCheckH =
        getGridCurrent("grid1").requiredValuesCheck();
      if (!requiredValuesCheckH) {
        return false;
      }

      // L 필수값 체크
      const requiredValuesCheckL =
        getGridCurrent("grid2").requiredValuesCheck();
      if (!requiredValuesCheckL) {
        return false;
      }

      const qtiohChanges = getGridCurrent("grid1").getChange();
      const qtioChanges = getGridCurrent("grid2").getChange();

      const headerInserts = qtiohChanges.insert ?? [];
      const lineInserts = qtioChanges.insert ?? [];
      if (headerInserts.length > 0 && lineInserts.length === 0) {
        await showDialog(
          "alter",
          "상세 정보가 없습니다.\n행추가하여 상세정보 작성 후 저장해 주시기 바랍니다."
        );
        return false;
      }

      if (
        qtiohChanges.modifiedRowcount === 0 &&
        qtioChanges.modifiedRowcount === 0
      ) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }

      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
      const noSlip = await applyAutoSlip({
        cd_column: "no_io",
        cd_module: "MTL",
        no_line: 18,
        dt: focusedRowNodeH.data.dt_io,
        girdrefs: [gridRefs.current["grid1"], gridRefs.current["grid2"]],
        inserts: [qtiohChanges, qtioChanges],
        cd_menu: Menuprops.cd_menu,
      });

      // 요청 데이터 구성
      const SaveData = {
        qtioh: qtiohChanges,
        qtio: qtioChanges,
      };

      // 저장 API 호출
      const ok = await fnSave("SN_MTL_SCT_REG_001_SAVE_01", SaveData);
      if (!ok) return false;

      // 성공적으로 저장된 후 초기화
      getGridCurrent("grid1").resetUpdate();
      getGridCurrent("grid2").resetUpdate();
      return true;
    } catch (error: any) {
      getGridCurrent("grid1").resetUpdate(true);
      getGridCurrent("grid2").resetUpdate(true);
      return false;
    }
  };
  //#endregion

  //#region GridH Events
  const onBeforeAddRowH = (): any => {
    try {
      const modifiedRowsH = getGridCurrent("grid1").getChange();
      if (modifiedRowsH.modifiedRowcount > 0) {
        showDialog("warning", "수정된 내용이 있습니다.");
        return false;
      }
      const searchInfo = searchRef.current.getFormdata();
      const today = dateToString(new Date());
      const updatedRow = {
        cd_firm: userInfo?.cd_firm,
        cd_biz: searchInfo?.cd_biz,
        cd_dept: userInfo?.cd_dept,
        nm_dept: userInfo?.nm_dept,
        cd_gp: userInfo?.cd_gp,
        cd_emp: userInfo?.cd_emp,
        nm_emp: userInfo?.nm_emp,
        dt_io: today,
        fg_trns: "1",
        rt_exch: 1,
        cd_exch: "000",
        tp_slip: "5",
        fg_slip: "2",
        fg_map: "3", // 외주
        am: 0,
        am_k: 0,
        am_vat: 0,
        am_supply: 0,
        no_hst: 0,
        no_io: "",
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onCellValueChangedH = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;
    const rowCount = getGridCurrent("grid2").getDisplayedRowCount();

    switch (colId) {
      case "cd_io":
        const ioType = iotypeRef.current.find(
          (item) => item.cd_io === rowData.cd_io
        );

        rowNode.setDataValue("fg_io", ioType.fg_io, "noEvent");
        rowNode.setDataValue("fg_money", ioType.fg_money, "noEvent");
        // rowNode.setDataValue("fg_map", ioType.fg_map, "noEvent");

        if (rowCount > 0) {
          getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
            node.setDataValue(colId, rowData.cd_io, "noEvent");
            node.setDataValue("fg_io", ioType.fg_io, "noEvent");
            node.setDataValue("fg_money", ioType.fg_money, "noEvent");
            // node.setDataValue("fg_map", ioType.fg_map, "noEvent");
          });
        }
        break;
      case "dt_io":
        getGridCurrent("grid2").forEachFilteredNode((node: RowNode) => {
          node.setDataValue(colId, rowData.dt_io, "noEvent");
        });
        break;
      case "cd_cust":
        rowNode.setDataValue("cd_wh_ref", rowData.cd_cust, "noEvent");
        rowNode.setDataValue("cd_cust_dlv", rowData.cd_cust, "noEvent");
        break;
    }
  };

  const onFocusedRowChangedH = (event: any) => {
    const selectedRow = event; // 클릭한 행 데이터
    if (selectedRow) {
      const no_io = selectedRow.no_io;
      getGridCurrent("grid2").selectAndFocusRow(0);
      setSelectedDocu(no_io);
    } else {
      return;
    }
  };

  const onGridHBtnsClick = async (id: string) => {
    // 신규행이 없으면 Dialog로 띄우기
    const qtiohChanges = getGridCurrent("grid1").getChange().insert;
    if (qtiohChanges.length === 0) {
      await showDialog("alter", "신규행을 추가하시기 바랍니다.");
      return;
    }

    switch (id) {
      case "btn_POP1":
        // searchPanel에서 선택된 값 전달
        const selectedFormData = searchRef.current.getFormdata();
        const selectedData = {
          cd_biz: selectedFormData.cd_biz,
          dt_from: selectedFormData.dt_from,
          dt_to: selectedFormData.dt_to,
        };

        // 신규행 포커스 잡아주기
        let focusedRowHIdx = 0;
        getGridCurrent("grid1").forEachFilteredNode((node: RowNode) => {
          if (node.data.state === "insert") {
            focusedRowHIdx = node.rowIndex ?? focusedRowHIdx;
          }
        });

        getGridCurrent("grid1").selectAndFocusRow(focusedRowHIdx);

        const component = (
          <SN_MTL_SCT_REG_001_POP_001 selectedData={selectedData} />
        );
        const result = await showResizablePopupModal({
          title: "요청적용",
          children: component,
          validation: true,
        });

        if (!result || result === "close") return;

        const headerResult = result[0];
        const lineResult = result[1];

        // 요청도움창 Header 결과 적용
        const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();
        if (focusedRowNodeH && headerResult[0]) {
          for (const key in headerResult[0]) {
            switch (key) {
              case "cd_io":
              case "cd_cust":
              case "nm_cust":
                focusedRowNodeH.setDataValue(key, headerResult[0][key]);
                break;
            }
          }
        }

        // 요청도움창 Line 결과 적용
        lineResult.map((result: any) => {
          if (headerResult[0]) {
            const addedNode = onAddRow();
            if (addedNode) {
              for (const key in result) {
                switch (key) {
                  case "no_line":
                    addedNode.setDataValue(
                      "no_line_req",
                      result.no_line,
                      "noEvent"
                    );
                    break;
                  case "no_po":
                    addedNode.setDataValue("no_order", result[key], "noEvent");
                    break;
                  case "no_line_pol":
                    addedNode.setDataValue(
                      "no_line_order",
                      result[key],
                      "noEvent"
                    );
                    break;
                  default:
                    addedNode.setDataValue(key, result[key], "noEvent");
                    break;
                }
              }
              addedNode.setDataValue("qt_good", result.qt_remain, "noEvent");
              addedNode.setDataValue("qt", result.qt_remain, "noEvent");
            }
          }
        });
        break;
    }
  };
  //#endregion

  //#region GridL Events
  const onBeforeAddRowL = (): any => {
    try {
      const focusedRowNodeH = getGridCurrent("grid1").focusedRowNode();

      const maxNoLine = getGridCurrent("grid2").getMaxValue("no_line");
      const updatedRow = {
        cd_firm: focusedRowNodeH.data?.cd_firm,
        cd_biz: focusedRowNodeH.data?.cd_biz,
        dt_io: focusedRowNodeH.data?.dt_io,
        no_line: maxNoLine + 1,
        no_io: focusedRowNodeH.data?.no_io,
        cd_io: focusedRowNodeH.data?.cd_io,
        fg_slip: focusedRowNodeH.data?.fg_slip,
        tp_slip: focusedRowNodeH.data?.tp_slip,
        fg_io: focusedRowNodeH.data?.fg_io,
        fg_money: focusedRowNodeH.data?.fg_money,
        fg_vat: "21",
        rt_exch: focusedRowNodeH.data?.rt_exch,
        fg_map: focusedRowNodeH.data?.fg_map,
        qt: 0,
        am: 0,
        am_k: 0,
        am_vat: 0,
        am_supply: 0,
        um: 0,
        um_k: 0,
        um_supply: 0,
        qt_good: 0,
        qt_bad: 0,
        yn_req: "Y",
        qt_cls: 0,
      };

      return updatedRow;
    } catch (error: any) {
      return false; // 추가 취소
    }
  };

  const onCellValueChangedL = async (event: any) => {
    if (!event) return;

    const colId = event.colId;
    const rowData = event.rowData;
    const rowNode = event.rowNode;

    switch (colId) {
      case "qt_good":
      case "qt_bad":
        rowNode.setDataValue("qt", rowData.qt_good + rowData.qt_bad, "noEvent");
        break;
    }
  };

  const onGridLBtnsClick = async (id: string) => {
    switch (id) {
      case "deleteRow":
        onDeleteRow();
        break;
    }
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddRowH,
          onCellValueChanged: onCellValueChangedH,
          onFocusedRowChanged: onFocusedRowChangedH,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        events: {
          onBeforeAddRow: onBeforeAddRowL,
          onCellValueChanged: onCellValueChangedL,
          onBtnDeleteClick: onGridLBtnsClick,
        },
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  //#region  UI
  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnResizeBar direction={"height"} id="panel1" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]} // Grid Name
            columnConfig={grids.grid1.columns} // 컬럼 설정 전달
            rowData={grids.grid1.dataSource} // Datasource
            etcProps={{ rowSelection: "single", title: "출고정보" }}
            events={grids.grid1.events}
            filter={{ refs: [gridRefs.current["grid2"]], columns: ["no_io"] }}
            header={
              <div>
                <SnButton
                  text={"요청적용"}
                  onClick={() => {
                    onGridHBtnsClick("btn_POP1");
                  }}
                />
              </div>
            }
          />
        </SnResizeBar>
        <SnResizeBar direction={"height"} id="panel2" orgSize="45.5%">
          <SnGrid
            ref={gridRefs.current["grid2"]} // Grid Name
            columnConfig={grids.grid2.columns} // 컬럼 설정 전달
            rowData={grids.grid2.dataSource} // Datasource
            etcProps={{ rowSelection: "multiple", title: "상세정보" }}
            events={grids.grid2.events}
          />
        </SnResizeBar>
        <SnFileUploadBox selectedDocu={selectedDocu} selectedLineDocu={0} />
      </div>
    </div>
  );
  //#endregion
});

export default SN_MTL_SCT_REG_001;
